import _ from 'lodash';
import React, { useCallback, useMemo, useState, memo } from 'react';

import { CreditCardSearchBar } from '../components/CreditCardSearchBar';
import { EmptyStatus } from '../components/EmptyStatus';
import { CardRewardsEarnRates, EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { useBestEarnRates } from '../data/shared/hooks/useBestEarnRates';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { EditMonthlySpend } from '../components/EditMonthlySpend';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap';
import { EditPointValues } from '../components/EditPointValues';
import { useCreditCardState } from '../hooks/useCreditCardState';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { TableCellCategory } from '../components/design-system/TableCellCategory';
import { TableCellWithSubtext } from '../components/design-system/TableCellWithSubtext';
import { useCardModal } from '../hooks/useCardModal';
import { CardComboTemplates } from '../components/CardComboTemplates';
import { EditableTableCell } from '../components/design-system/EditableTableCell';
import { CategoryModal } from '../components/CategoryModal';
import { usePointValues } from '../data/shared/hooks/usePointValues';
import { useMonthlySpendState } from '../data/shared/hooks/useMonthlySpendState';
import { useShareModal } from '../hooks/useShareModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { flattenObject } from '../data/shared/utils';
import { CardButton } from '../components/design-system/CardButton';
import { CardCarousel } from '../components/CardCarousel';
import { Stepper } from '../components/design-system/Stepper';
import { TableCardButton } from '../components/TableCardButton';
import { CopyButton } from '../components/CopyButton';
import { Navigation } from '../components/Navigation';
import { PageWrapper } from '../components/design-system/PageWrapper';
import { COLUMN_WIDTH } from '../constants';
import { ContentWrapper } from '../components/design-system/ContentWrapper';
import { CreditCardData } from 'data/shared/CreditCardData';
import { CreditCard } from '../data/shared/CreditCard';
import { CreditCardRewardsType } from '../data/shared/CreditCardRewardsTypes';
import { PopularCards } from '../components/PopularCards';

declare module 'lodash' {
  interface LoDashStatic {
    toPairsDeep<T>(obj: T): Array<[string, any]>;
  }
}

_.mixin({
  toPairsDeep: (obj: any): Array<[string, any]> =>
    _.flatMap(_.toPairs(obj), ([k, v]) =>
      _.isObjectLike(v) ? _.toPairsDeep(v) : [[k, v]]
    ),
});

const useCreditCardRewards = (cards: CreditCard[]) => {
  return useMemo(
    () => _.mapValues(
      _.pick(CreditCardData, cards),
      (obj) => obj.rewards_earn_rates
    ),
    [cards]
  );
};

export const RewardsEstimator = () => {
  const [selectedCards, setSelectedCards] = useCreditCardState();

  const handleRemoveCard = useCallback(
    (cardToRemove: CreditCard) => {
      setSelectedCards((currentCards: CreditCard[]) =>
        currentCards.filter((card) => card !== cardToRemove)
      );
    },
    [setSelectedCards]
  );

  const handleAddCard = useCallback(
    (newCard: CreditCard) => {
      setSelectedCards((currentCards: CreditCard[]) => {
        if (currentCards.find((card) => card === newCard)) {
          return currentCards;
        }
        return [...currentCards, newCard];
      });
    },
    [setSelectedCards]
  );

  const [cardModal, setShowCardModal] = useCardModal(handleRemoveCard);
  const [shareModal, showShareModal] = useShareModal();

  return (
    <>
      {shareModal}
      <Navigation />

      <ContentWrapper style={{ backgroundColor: '#fff', position: 'relative' }}>
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{ fontSize: 64, lineHeight: 1.1, 
            // color: '#3F79A4' 
            }}>
            There is no best credit card.
          </h1>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              // alignItems: 'center',
              gap: 10,
              paddingTop: 30,
            }}
          >
            <div style={{ minWidth: 300, fontSize: 18, paddingTop: 10 }}>
              <p>All rewards cards have strengths and weaknesses.</p>
              <p>
                To get the best rewards, we'll help build a wallet customized
                for you, then automate the rewards process.
              </p>
            </div>
            <div>
              <CardCarousel />
            </div>
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            // bottom: -200,
            left: 0,
            right: 0,
            height: 400,
            zIndex: 1,
            background: 'linear-gradient(#fff, #eef1f4)',
          }}
        ></div>
      </ContentWrapper>
      <PageWrapper style={{ zIndex: 3, position: 'relative' }}>
        <div style={{}}>
          <Stepper step={1} />
          <h2>Choose your cards</h2>
          {/* <p style={{ fontSize: 18 }}>
            Add your cards to see how to get the best rewards.
          </p> */}
        </div>
        <div style={{ margin: '10px 0' }}>
          <CardComboTemplates setSelection={setSelectedCards} selectedCards={selectedCards} />
        </div>
        <div
          style={{
            position: 'relative',
            maxWidth: 1200,
          }}
        >
          <CreditCardSearchBar
            setSelectedCards={setSelectedCards}
            handleAddCard={handleAddCard}
            handleRemoveCard={handleRemoveCard}
            selectedCards={selectedCards}
          />
        </div>
        <div style={{ flexGrow: 1 }}>
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              flexWrap: 'wrap',
            }}
          >
            <h3 style={{ margin: 20, marginLeft: 0, }}>This is<br /> your wallet:</h3>
            {selectedCards.map((card) => {
              return (
                <CardButton
                  cardData={CreditCardData[card]}
                  onClick={() => setShowCardModal(card)}
                />
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 80 }}>
          <Stepper step={2} />
          <h2>View categories</h2>
          <p>
            Based on your wallet, here's how to get the best rewards for your
            spend.
          </p>
        </div>

        <div style={{ position: 'relative', zIndex: 0, marginTop: 12 }}>
          <RewardsCardPreviews
            cards={selectedCards}
            handleRemoveCard={handleRemoveCard}
            handleAddCard={handleAddCard}
            setSelectedCards={setSelectedCards}
          />
        </div>

        {/* <div style={{ marginTop: 140 }}>
          <Stepper step={3} />
          <h1>Pick additional cards</h1>
          <p>
            Based on a combination of our ratings, rewards strengths, and to
            complement your current wallet.
          </p>

          <CardRecommendations cards={selectedCards} />
        </div>
 */}
        <div style={{ marginTop: 140 }}>
          <Stepper step={3} />
          <h2>Save your wallet</h2>
          <p>
            Copy this link to save your wallet and to come back to it anytime,
            or share it with others
          </p>
          <CopyButton textToCopy={window.location.href} />
        </div>

        {/* <iframe
          style={{
            width: '100%',
            maxWidth: '100%',
            border: 'none',
            height: 500,
          }}
          title="Email signup form"
          src="https://cdn.forms-content-1.sg-form.com/6a4d5443-b372-11ef-a2b9-62b0eabd2820"
        />
        */}

        {/* Footer */}
        {/* <div style={{ marginTop: 100 }}>
          <p>
            SmartWallet is a free tool to help you get the best rewards for your
            credit cards.
          </p>
          <p>We're not affiliated with any credit card companies.</p>
          <p>
            Our data is independently gathered and reviewed by our team, but we
            may not always get it right. If you find an error, please let us
            know.
          </p>
        </div> */}
        {cardModal}
      </PageWrapper>
    </>
  );
};

export const RewardsCardPreviews = ({
  cards,
  handleRemoveCard,
  handleAddCard,
  setSelectedCards,
}: {
  cards: CreditCard[];
  handleRemoveCard: (card: CreditCard) => void;
  handleAddCard: (card: CreditCard) => void;
  setSelectedCards: (cards: CreditCard[]) => void;
}) => {
  const [inUSA, setInUSA] = useState(true);
  const [monthlySpend, setMonthlySpend, resetMonthlySpendValues] = useMonthlySpendState(null, () => {});
  const { pointValues,  setPointValues, resetPointValues } =
    usePointValues(cards, true, () => {}, {});
  const rewardsByCard = useCreditCardRewards(cards);
  const bestEarnRates = useBestEarnRates(cards, monthlySpend, pointValues, !inUSA);

  const [showEditMonthlySpend, setShowEditMonthlySpend] = useState(false);
  const [showEditPointValues, setShowEditPointValues] = useState(false);
  const [showCategoryModalCategory, setShowCategoryModalCategory] = useState();
  const [customizePointAndSpendValues, setCustomizePointAndSpendValues] =
    useState(false);
  const [cardModal, setShowCardModal] = useCardModal(handleRemoveCard);

  const allKeys = Object.keys(flattenObject(monthlySpend));
  const bestEarnRatesByCard = useMemo(() => 
    _.groupBy(
      allKeys.flatMap((key) => {
        const maybeEarnRate = _.get(bestEarnRates, key)?.[0];
        if (maybeEarnRate) {
          return [{ ...maybeEarnRate, key }];
        } else {
          return [];
        }
      }),
      'card'
    ),
    [allKeys, bestEarnRates]
  );

  const totalSpend = useMemo(() => _.sum(Object.values(flattenObject(monthlySpend))), [monthlySpend]);
  const totalRewards =
    useMemo(() => {
      return _.sum(
      Object.entries(flattenObject(bestEarnRates))
        .filter(([k, v]) => Boolean(v[0]))
        .map(([key, value]: [string, EarnRateInfo[]]) => {
          return Math.round(
            value[0].earnRate *
              pointValues[value[0].rewardsType] *
              (_.get(monthlySpend, key) ?? 0)
          );
        })
      ) / 100;
    },
    [monthlySpend, pointValues, bestEarnRates]
  );
  const cardCountToCarry = useMemo(() => Object.keys(rewardsByCard).length, [rewardsByCard]);
  const categoryCount = useMemo(() => Object.values(bestEarnRatesByCard).flatMap(
    (v) => v
  ).length, [bestEarnRatesByCard]);

  const handlePointValuesChange = useCallback((rewardsType: CreditCardRewardsType, value: string) => {
    setPointValues(rewardsType, value);
  }, [setPointValues]);

  const handleMonthlySpendChange = useCallback((key: string, value: string) => {
    setMonthlySpend((prevMonthlySpend: CardRewardsEarnRates) => ({
      ..._.set(prevMonthlySpend, key, Number(value)),
    }));
  }, [setMonthlySpend]);

  return (
    <>
      {cardModal}
      {showCategoryModalCategory && (
        <CategoryModal
          cards={cards}
          category={showCategoryModalCategory}
          monthlySpend={monthlySpend}
          pointValues={pointValues}
          handleClose={() => setShowCategoryModalCategory(undefined)}
        />
      )}
      {showEditMonthlySpend && (
        <EditMonthlySpend
          monthlySpend={monthlySpend}
          setMonthlySpend={setMonthlySpend}
          resetMonthlySpend={resetMonthlySpendValues}
          handleClose={() => setShowEditMonthlySpend(false)}
        />
      )}
      {showEditPointValues && (
        <EditPointValues
          cards={cards}
          pointValues={pointValues}
          setPointValues={setPointValues}
          resetPointValues={resetPointValues}
          handleClose={() => setShowEditPointValues(false)}
        />
      )}
      {!cards.length && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            minWidth: COLUMN_WIDTH,
            minHeight: 300,
          }}
        >
          <h3>No cards selected. Try one of these cards:</h3>
          <div>
            <PopularCards
              onAddCard={handleAddCard}
              onRemoveCard={handleRemoveCard}
              selectedCards={cards}
              showTabs={false}
            />
          </div>
        </div>
      )}

      {cards.length > 0 && (
        <div style={{}}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
              margin: 10,
              gap: 20,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 10,
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: '-10px -10px 10px 0',
                padding: '0 10px',
                borderRadius: 10,
                backgroundColor: '#eef1f4',
              }}
            >
              <p>Advanced:</p>
              <div>
                <input
                  id="customizePointAndSpendValues"
                  type="checkbox"
                  checked={customizePointAndSpendValues}
                  onChange={() =>
                    setCustomizePointAndSpendValues(
                      !customizePointAndSpendValues
                    )
                  }
                />
                <label htmlFor="customizePointAndSpendValues">
                  Show point and spend values
                </label>
              </div>
              <button
                onClick={() => {
                  resetPointValues();
                  resetMonthlySpendValues();
                  setCustomizePointAndSpendValues(false);
                }}
              >
                <FontAwesomeIcon icon={faRefresh} style={{ marginRight: 5 }} />
                Reset
              </button>
              <button onClick={() => setInUSA(!inUSA)}>
              {inUSA
                ? '🇺🇸 In USA - no foreign transaction fees'
                : '🌍 Outside USA - include foreign transaction fees'}
            </button>
            </div>
            <div>
              <h3 style={{ margin: 0 }}>Categories</h3>
              <p>For each category below, use the following card:</p>
            </div>
          </div>
          <table style={{ borderSpacing: 10, margin: -10, width: '100%' }}>
            <thead style={{}}>
              <th style={{ textAlign: 'left', paddingLeft: 10 }}>Category</th>
              <th style={{ textAlign: 'left', paddingLeft: 20 }}>Earn rate</th>
              <th style={{ textAlign: 'left' }}>Recommended card</th>
              {/* <th>Owned</th> */}
              <th>Annual fee</th>
              {customizePointAndSpendValues && (
                <>
                  <th>
                    <button onClick={() => setShowEditPointValues(true)}>
                      Cents per point
                    </button>
                  </th>
                  <th>Cents per dollar spent</th>
                  <th>Monthly spend</th>
                  <th>Monthly rewards</th>
                </>
              )}
            </thead>
            <tbody>
              {cards.map((card) => {
                const bestEarnRatesForCard = bestEarnRatesByCard[card] || [];
                return bestEarnRatesForCard.map((earnRateInfo) => {
                  const { rewardsType, earnRate, key, spend } = earnRateInfo;
                  return (
                    <tr key={key + card}>
                      <td>
                        <button
                          onClick={() => setShowCategoryModalCategory(key)}
                        >
                          <TableCellCategory
                            text={_.get(categoryEmojiMap, key)}
                            subText={formatSpendCategory(key)}
                          />
                        </button>
                      </td>
                      <td>
                        <button
                          style={{ width: 110 }}
                          onClick={() => setShowCategoryModalCategory(key)}
                        >
                          <TableCellWithSubtext
                            text={formatEarnRate(earnRate, rewardsType)}
                            subText={formatRewardsType(rewardsType)}
                          />
                        </button>
                      </td>
                      <td>
                        <TableCardButton
                          cardData={CreditCardData[card]}
                          onClick={() => setShowCardModal(card)}
                        />
                      </td>
                      {/* <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" />
                    </td> */}
                      <td style={{ textAlign: 'center' }}>
                        $
                        {CreditCardData[card]?.fees
                          ?.annual_fee_amount_dollars ?? 0}
                      </td>
                      {customizePointAndSpendValues && (
                        <>
                          <td style={{ textAlign: 'center' }}>
                            {rewardsType === 'cash_back' ? (
                              1
                            ) : (
                              <EditableTableCell
                                style={{ width: 120 }}
                                suffix=" cpp"
                                value={String(
                                  pointValues[
                                    rewardsType satisfies CreditCardRewardsType
                                  ]
                                )}
                                onChange={(value) =>
                                  setPointValues(rewardsType, value)
                                }
                              />
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {Math.round(
                              earnRate * pointValues[rewardsType] * 100
                            ) / 100}
                            ¢
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {/* General must open a modal that allows editing eveything else, since it
                            includes all values added together */}
                            {key === 'allPurchases' ? (
                              <button
                                onClick={() => setShowEditMonthlySpend(true)}
                              >
                                <TableCellWithSubtext
                                  text={`$${spend}`}
                                  subText="On everything else"
                                />
                              </button>
                            ) : (
                              <EditableTableCell
                                prefix="$"
                                value={spend}
                                style={{ width: 150 }}
                                onChange={(value) => {
                                  setMonthlySpend(
                                    (
                                      prevMonthlySpend: CardRewardsEarnRates
                                    ) => {
                                      // The spread is required as _.set updates the original object.
                                      return {
                                        ..._.set(
                                          prevMonthlySpend,
                                          key,
                                          Number(value)
                                        ),
                                      };
                                    }
                                  );
                                }}
                              />
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            $
                            {Math.round(
                              spend * earnRate * pointValues[rewardsType]
                            ) / 100}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                });
              })}
              <div style={{ marginTop: 40 }} />

              <tr style={{ fontSize: 24 }}>
                <td style={{ width: 100 }}>
                  <p
                    style={{
                      fontSize: 18,
                      margin: 0,
                      fontWeight: 'bold',
                      lineHeight: 1.1,
                    }}
                  >
                    Totals:
                  </p>
                </td>
                <td>
                  <TableCellWithSubtext
                    text={categoryCount}
                    subText="Category count"
                    style={{ textAlign: 'left' }}
                  />
                </td>
                <td>
                  <TableCellWithSubtext
                    text={cardCountToCarry}
                    subText="Cards carried in wallet"
                    style={{ textAlign: 'left' }}
                  />
                </td>
                {/* <td></td> */}
                <td style={{ textAlign: 'left' }}>
                  <TableCellWithSubtext
                    text={`$${_.sum(
                      cards.map(
                        (card) =>
                          CreditCardData[card]?.fees
                            ?.annual_fee_amount_dollars ?? 0
                      )
                    )}`}
                    subText="Total annual fees"
                  />
                </td>
                {customizePointAndSpendValues && (
                  <>
                    <td></td>
                    <td>
                      <TableCellWithSubtext
                        text={`${
                          Math.round((totalRewards / totalSpend) * 10000) / 100
                        }¢`}
                        subText="per dollar"
                      />
                    </td>
                    <td>
                      <button onClick={() => setShowEditMonthlySpend(true)}>
                        <TableCellWithSubtext
                          text={`$${totalSpend}`}
                          subText="Monthly spend"
                        />
                      </button>
                    </td>
                    <td>
                      <TableCellWithSubtext
                        text={`$${totalRewards}`}
                        subText="Monthly rewards value"
                      />
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
